import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { group as radashGroup, omit as radashOmit } from 'radash';
import { v4 as uuidv4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { LoaderModalComponent } from '@connect-services/client/components/modals/loader-modal/loader-modal.component';
import {
    BayDrawer,
    Cassettes,
    FiberingSideEnum,
    RestoreActionEnum,
    SelectedNode,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { CreateDrawerModalResult } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/create-drawer-modal/create-drawer-modal.component';
import { SynopticPlanEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/synoptic-plan-edit.component';
import { SynopticPlanEditHeaderComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/synoptic-plan-edit-header/synoptic-plan-edit-header.component';
import {
    CassetteReferencePosition,
    FiberingResult,
    PlanTypeEnum,
    SynopticBayPlanItem,
    SynopticBoxPlanCableBySide,
} from '@connect-services/client/sdk/api';
import { BoxPlanEditModeStateService } from '@connect-services/client/services/synoptic/edit/box-plan-edit-mode-state.service';
import { SynopticBayPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-bay-plan-edit.service';
import { SynopticPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { SynopticPlanEditApiCallerService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-api-caller.service';
import { SynopticPlanEditModalService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-modal.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticPlanBuilderService } from '@connect-services/client/services/synoptic/synoptic-plan-builder.service';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoadingComponent,
        RessourcesModule,
        SynopticPlanEditComponent,
        SynopticPlanEditHeaderComponent,
        TabComponent,
        TabContentDirective,
    ],
    providers: [SynopticBayPlanEditService],
    selector: 'app-synoptic-bay-plan-edit',
    styleUrl: './synoptic-bay-plan-edit.component.scss',
    templateUrl: './synoptic-bay-plan-edit.component.html',
})
export class SynopticBayPlanEditComponent implements OnInit {
    public readonly depositId = input.required<string>();
    public readonly layerId = input.required<string>();
    public readonly leaveEdition = output<void>();
    public readonly selectedNode = input.required<SelectedNode>();

    protected readonly bayTabs = computed<TabWithState[]>(() => {
        return this.getBayTabs(this.synopticBayPlanEditService.drawers());
    });
    protected readonly drawerView = viewChild<SynopticPlanEditComponent>('drawerView');
    protected readonly errors = signal<string[]>([]);
    protected readonly isLoading = signal<boolean>(true);
    protected readonly selectedDrawerIndex = signal<number>(0);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly PlanTypeEnum = PlanTypeEnum;
    protected readonly RestoreActionEnum = RestoreActionEnum;
    protected readonly synopticBayPlanEditService = inject(SynopticBayPlanEditService);

    private readonly currentDrawerId = computed<string | null | undefined>(() => {
        return this.synopticBayPlanEditService.drawers()[this.selectedDrawerIndex()]?.id;
    });

    private readonly projectId = computed<string>(() => this.projectBloc.value()?.id ?? '');
    private readonly tabView = viewChild<TabComponent>('tabView');

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly boxPlanEditModeStateService = inject(BoxPlanEditModeStateService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly selectSynopticService = inject(SelectSynopticService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticPlanBuilderService = inject(SynopticPlanBuilderService);
    private readonly synopticPlanEditApiCallerService = inject(SynopticPlanEditApiCallerService);
    private readonly synopticPlanEditModalService = inject(SynopticPlanEditModalService);
    private readonly synopticPlanEditService = inject(SynopticPlanEditService);
    private readonly translocoService = inject(TranslocoService);

    public ngOnInit(): void {
        this.boxPlanEditModeStateService.clearEditModeState(this.projectId());
        void this.setInitialBay();
    }

    protected automaticFibering(fiberingSide: FiberingSideEnum): void {
        this.drawerView()?.automaticFibering(fiberingSide);
    }

    protected async checkFibering(): Promise<void> {
        const errors = await this.synopticPlanEditApiCallerService.checkFibering(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            this.synopticBayPlanEditService.cassettes().flat(),
            PlanTypeEnum.BAY,
            this.selectSynopticService.selectedSynoptic()?.znUuid ?? '',
        );
        this.errors.set(errors);
    }

    protected async createDrawer(): Promise<void> {
        const value = await this.synopticPlanEditModalService.openCreateDrawerModal(this.depositId());
        if (!value) {
            return;
        }

        const newCassettes = this.getNewCassettes(value);
        this.synopticBayPlanEditService.cassettes.update((cassettes: SynopticBayPlanItem[][]) => {
            return [...cassettes, ...newCassettes];
        });
        requestAnimationFrame(() => {
            this.tabView()?.onTabClick(this.bayTabs().length - 1);
        });

        this.boxPlanEditModeStateService.updateEditModeState({
            cables: { enteringCables: [], outgoingCables: [] },
            cassettes: newCassettes,
            drawerId: newCassettes[0][0].ti_uuid,
            initialCables: { enteringCables: [], outgoingCables: [] },
            initialCassettes: [],
            layerId: this.layerId(),
            node: this.selectedNode().dataNode,
            projectId: this.projectId(),
        });
    }

    protected async onLeaveEdition(): Promise<void> {
        const shouldLeaveEdition = await this.synopticPlanEditService.leaveEdition(
            this.projectId(),
            this.layerId(),
            this.selectedNode().dataNode.uuid,
        );

        if (!shouldLeaveEdition) {
            return;
        }

        this.leaveEdition.emit();
    }

    protected resetFibering(fiberingSide: FiberingSideEnum): void {
        this.drawerView()?.resetFibering(fiberingSide);
    }

    protected restoreUndoEdition(action: RestoreActionEnum): void {
        const eventItem = this.boxPlanEditModeStateService.getEventCanceledOrRestored(action, this.projectId());
        if (!eventItem) {
            return;
        }

        this.drawerView()?.isRestoreUndoEdition.set(true);
        this.synopticBayPlanEditService.updateDrawerCables(eventItem['cables'], eventItem['drawerId']);
        this.synopticBayPlanEditService.updateDrawerCassettes(eventItem['cassettes'], eventItem['drawerId']);

        if (this.currentDrawerId() !== eventItem['drawerId']) {
            this.snackBarService.openInfoSnackBar('synopticPlan.updatesAppliedOnAnotherDrawer', { TYPE: action });
        }
        requestAnimationFrame(() => {
            this.drawerView()?.isRestoreUndoEdition.set(false);
        });
    }

    protected updateCassettesPositions(): void {
        this.drawerView()?.updateCassettesPositions();
    }

    protected updateDrawerCassettes(newCassettes: SynopticPlanItem[][], drawerId: string | null): void {
        this.synopticBayPlanEditService.updateDrawerCassettes(newCassettes as SynopticBayPlanItem[][], drawerId);
    }

    protected updateDrawerSideCables(cables: SynopticBoxPlanCableBySide, drawerId: string | null): void {
        this.synopticBayPlanEditService.updateDrawerCables(cables, drawerId);
    }

    protected async validateFibering(): Promise<void> {
        const dialogRef = this.matDialog.open(LoaderModalComponent, {
            autoFocus: false,
            data: {
                title: this.translocoService.translate('synopticPlan.reviewAndSave'),
            },
            minWidth: '40vw',
        });

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.setMessage(
                `${this.translocoService.translate('synopticPlan.validatingUpdates')}...`,
            );
        }

        const validationResult: FiberingResult = await this.synopticPlanEditApiCallerService.validateFibering(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            this.synopticBayPlanEditService.cassettes().flat(),
            PlanTypeEnum.BAY,
            this.selectSynopticService.selectedSynoptic()?.znUuid ?? '',
        );
        this.errors.set(validationResult.errors);

        if (!validationResult.extraObjects?.length || !validationResult.success) {
            dialogRef.componentInstance.setHasError(true);
            dialogRef.componentInstance.setMessage(this.translocoService.translate('synopticPlan.checkFiberingError'));
            setTimeout(() => {
                dialogRef.close();
            }, 2000);

            return;
        }

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.setMessage(
                `${this.translocoService.translate('synopticPlan.savingUpdates')}...`,
            );
        }

        try {
            await this.boxPlanEditModeStateService.saveFiberingUpdatesIntoProjectState(
                this.projectId(),
                this.depositId(),
                validationResult.extraObjects,
            );
        } catch {
            this.snackBarService.openErrorSnackBar('synopticPlan.checkFiberingError');
        } finally {
            dialogRef.close();
        }
    }

    private getBayTabs(drawers: BayDrawer[]): TabWithState[] {
        const translatedDrawer = this.translocoService.translate('synopticPlan.drawer');

        return drawers.map((drawer: BayDrawer) => {
            return {
                active: true,
                canCreate: false,
                name: `${translatedDrawer} ${drawer.position} - ${drawer.side}`,
            };
        });
    }

    private getNewCassettes(createDrawerModalResult: CreateDrawerModalResult): SynopticBayPlanItem[][] {
        const drawerReferenceData = {
            ...radashOmit(createDrawerModalResult.drawer, ['cassettesReferences']),
            drawerId: uuidv4(),
            position: createDrawerModalResult.position,
            side: createDrawerModalResult.side,
        };

        return createDrawerModalResult.drawer.cassettesReferences.reduce(
            (acc: SynopticBayPlanItem[][], cassetteReference: CassetteReferencePosition) => {
                return this.synopticPlanEditService.addCassette(
                    acc,
                    cassetteReference,
                    drawerReferenceData,
                ) as SynopticBayPlanItem[][];
            },
            [],
        );
    }

    private setCassettes(bayPlan: SynopticBayPlanItem[]): void {
        const groupedCassettes = radashGroup(
            bayPlan,
            (cassetteItem: SynopticBayPlanItem) => cassetteItem.cassette ?? '',
        ) as Cassettes<SynopticBayPlanItem>;
        const sortedCassettes = this.synopticPlanBuilderService.sortCassettes(
            groupedCassettes,
        ) as SynopticBayPlanItem[][];

        this.synopticBayPlanEditService.cassettes.set(sortedCassettes);
    }

    private async setInitialBay(): Promise<void> {
        const znUuid = this.selectSynopticService.selectedSynoptic()?.znUuid;
        if (!znUuid) {
            return;
        }

        const bayPlan = await this.synopticPlanEditApiCallerService.getBayPlan(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            znUuid,
        );
        const bayIds = [
            ...new Set(
                bayPlan
                    .map((item: SynopticBayPlanItem) => item.ba_uuid)
                    .filter((bayId: string | null): bayId is string => !!bayId),
            ),
        ];
        const cables = await this.synopticPlanEditApiCallerService.getSideCables(bayIds, this.depositId());

        this.synopticBayPlanEditService.sideCables.set(cables);
        this.setCassettes(bayPlan);

        this.isLoading.set(false);
    }
}
